import type { Cohort, Page } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import { addOne } from '@efdigital/ll-admin-v2/helpers/add-one.ts';
import { generateRandomSlug } from '@efdigital/ll-admin-v2/lib/random-slug';
import type { ParamsProgramId } from '@efdigital/ll-admin-v2/routes/programs/program';
import type { ParamsCohortId } from '@efdigital/ll-admin-v2/routes/programs/program/cohort';
import { array } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { set } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { DragToSort } from '../drag-to-sort.gts';

export class EditCohortPages extends Component<
  EditRecordSignature<Cohort, { params: ParamsProgramId & ParamsCohortId }>
> {
  @service declare store: Store;

  setPageValue = (property: string, page: Page, event: Event) => {
    const target = event.target as HTMLInputElement;
    set(page, property, target.value);
  };

  removePageAt = (pages: Page[], index: number) => {
    const removed = pages[index];
    if (removed) {
      // Immediate rollback before removing from relationship
      removed.rollbackAttributes();
    }
    pages.splice(index, 1);
  };

  newPage = async () => {
    const cohort = this.args.item;
    const client = await cohort?.client;

    const page = this.store.createRecord<Page>(DataTypeContent.Page, {
      slug: generateRandomSlug(),
      template: 'widgets',
      category1: 'Xceed',
      category2: 'Cohort',
      category3: this.args.params.program_id,
      category4: this.args.params.cohort_id,
      verb: 'http://adlnet.gov/expapi/verbs/experienced',
      activityType: 'http://adlnet.gov/expapi/activities/page',
      client,
      systemLineage: 'll-admin-v2',
    });

    const pages = await this.args.item.pages;
    pages.push(page);
  };

  <template>
    <EditRecord
      @editLabel="Manage Cohort-Level Pages"
      @item={{@item}}
      @context={{@context}}
      @maxDepth={{2}}
      ...attributes
    >
      <:form as |uniqueId isDisabled isReadOnly|>
        <fieldset disabled={{isDisabled}} class="mb-0" id="{{uniqueId}}-pages">
          <legend class="gud-eyebrow">Pages</legend>

          <p class="form-text">Pages are the individual content pages that make
            up the cohort and are displayed in the top navigation. You should
            create pages for the introduction, calendar, and other high level
            content.
          </p>

          {{#if isReadOnly}}
            <Await @promise={{@item.pages}}>
              <:success as |pages|>
                {{#if pages.length}}
                  <div class="list-group mb-3">
                    {{#each pages as |page|}}
                      <LinkTo
                        @route="programs.program.cohort.content.page"
                        @models={{array
                          @params.program_id
                          @params.cohort_id
                          page.id
                        }}
                        class="list-group-item"
                      >{{page.title}}
                        <iconify-icon inline icon="ph:arrow-right" /></LinkTo>
                    {{/each}}
                  </div>
                {{else}}
                  <p>No pages have been created yet.</p>
                {{/if}}
              </:success>
            </Await>
          {{else}}
            <Await @promise={{@item.pages}}>
              <:success as |pages|>
                <div class="list-group mb-3 gap-1">
                  <DragToSort @items={{pages}}>
                    <:item as |dropZone draggable page index|>
                      <div class="input-group" {{dropZone index}}>
                        <span
                          class="input-group-text justify-content-center fw-bold drag-handle w-3rem"
                          {{draggable page index}}
                          title="Drag to reorder"
                        >
                          {{addOne index}}
                        </span>
                        <input
                          type="text"
                          class="form-control list-group-item"
                          value={{page.title}}
                          aria-label="Page title"
                          required
                          {{debounce (fn this.setPageValue "title" page)}}
                        />
                        <button
                          aria-label="Remove page"
                          class="btn btn-outline-primary rounded-end-1"
                          type="button"
                          {{on "click" (fn this.removePageAt pages index)}}
                        ><iconify-icon inline icon="ph:trash-bold" /></button>
                      </div>
                    </:item>
                  </DragToSort>
                </div>
              </:success>
            </Await>
          {{/if}}
        </fieldset>
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <button
            type="button"
            class="btn btn-primary btn-sm"
            id="{{uniqueId}}-newPage"
            {{on "click" this.newPage}}
          >Add Page</button>
        {{/unless}}
      </:buttons>
    </EditRecord>
  </template>
}
